import { useEffect, useState } from "react";
import { Flame, Sparkles, BookOpen, Check } from "lucide-react";

const PHASES = [
  { label: "Re-reading your memories…", icon: BookOpen },
  { label: "Reorganizing chapters…", icon: Sparkles },
  { label: "Verifying every detail is preserved…", icon: Flame },
  { label: "Tying up narrative loose ends…", icon: Sparkles },
];

const BakingLoader = ({ result, onClose }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (result) return; // stop cycling once done
    const interval = setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [result]);

  return (
    <div className="fixed inset-0 z-[120] bg-[#1a1a2e]/95 backdrop-blur-md flex items-center justify-center p-6" data-testid="baking-loader">
      <div className="max-w-md w-full text-center">
        {!result ? (
          <>
            {/* Animated flame crest */}
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/40 to-rose-500/40 blur-2xl animate-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 flex items-center justify-center shadow-2xl">
                <Flame className="w-10 h-10 text-white" strokeWidth={2} />
              </div>
            </div>
            <h2 className="font-serif text-3xl text-white mb-3">Baking your story</h2>
            <p className="font-sans text-sm text-white/60 mb-8">
              The AI is performing a final review across every chapter and raw note. This takes 20–60 seconds.
            </p>
            <ul className="space-y-3 text-left" data-testid="baking-phases">
              {PHASES.map((p, i) => {
                const Icon = p.icon;
                const active = i === phase;
                const done = i < phase;
                return (
                  <li
                    key={i}
                    className={`flex items-center gap-3 transition-all ${
                      active ? "text-amber-300" : done ? "text-white/40" : "text-white/25"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      active ? "bg-amber-300/20 ring-2 ring-amber-300/60 animate-pulse" : done ? "bg-white/5" : "bg-white/5"
                    }`}>
                      {done ? <Check className="w-3.5 h-3.5 text-white/60" /> : <Icon className="w-3.5 h-3.5" />}
                    </span>
                    <span className="font-sans text-sm">{p.label}</span>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <>
            {/* Done state */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-emerald-400/30 blur-2xl" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-600 flex items-center justify-center shadow-2xl">
                <Check className="w-10 h-10 text-white" strokeWidth={3} />
              </div>
            </div>
            <h2 className="font-serif text-3xl text-white mb-2">Your story is fully baked</h2>
            <p className="font-sans text-base text-emerald-200/90 italic mb-6">
              …and preserved for legacy.
            </p>

            <div className="bg-white/5 rounded-2xl border border-white/10 p-5 text-left mb-6" data-testid="bake-summary">
              {result.summary && <p className="font-sans text-sm text-white/80 mb-3 leading-relaxed">{result.summary}</p>}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="text-center">
                  <p className="font-serif text-2xl text-amber-300">{result.chapters_reordered ?? 0}</p>
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/50">reordered</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl text-amber-300">{result.chapters_merged ?? 0}</p>
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/50">merged</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl text-amber-300">{result.details_recovered ?? 0}</p>
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/50">details saved</p>
                </div>
              </div>
              {result.loose_ends?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-2">Noted loose ends</p>
                  <ul className="space-y-1">
                    {result.loose_ends.slice(0, 3).map((le, i) => (
                      <li key={i} className="font-sans text-xs text-white/70 leading-relaxed">· {le}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#1a1a2e] font-sans font-semibold text-sm transition-colors"
              data-testid="bake-done-btn"
            >
              Continue writing
            </button>
            <p className="text-[10px] font-sans text-white/40 mt-3">You can still add more chapters — this was a checkpoint.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default BakingLoader;
